import { useEffect, useState, useRef } from 'react';
import { fieldReader, fieldWriter, fieldWriterExternal } from '../../services/loanApi';
import { getAccessToken } from "../../services/authApi";
import Modal from '../shared/Modal';
import Toast from '../shared/Toast';
import LoadingOverlay from '../shared/LoadingOverlay';
// Remove static import, will load dynamically
import AdditionalInfoControl from './AdditionalInfoControl';
import { error as logAppInsightError } from '../../utils/logger';
import { logError } from "../../utils/logError";
import BorrowerPairSection from '../commercial/BorrowerPairSection';
import { redirectToPage } from '../menu/navigation';
import { evaluateAndRemoveIncomes } from '../../services/borrowerApi';
import DirtyCheckPrompt from "../shared/DirtyCheckPrompt";
import { getBPCPrograms, fetchLoCompHistoryFlag } from '../../services/userApi';
import { getAdditionalInfoFields } from '../../config/organizations';
import { LOAN_EDIT_LOCK_FIELD_IDS, isLoanEditLocked } from '../../utils/loanEditLock';

const DIRTY_FLAG_KEY = "loan_additional_info_dirty";

const btnClass = (disabled) =>
    `btn-base btn-save ${disabled ? "cursor-not-allowed opacity-60" : ""}`;

const btnNextClass = (disabled) =>
    `btn-base btn-next ${disabled ? "cursor-not-allowed opacity-60" : ""}`;

function ActionButtons({
    isReadOnly,
    showNext,
    onSave,
    onNext,
    containerClass = "",
    disableNextWhenReadOnly = false,
}) {
    return (
        <div className={`flex gap-2 ${containerClass}`}>

            <button onClick={() => onSave(false)} className={btnClass(isReadOnly)} disabled={isReadOnly}>
                Save
            </button>

            {showNext && (
                <button
                    onClick={onNext}
                    className={btnNextClass(disableNextWhenReadOnly && isReadOnly)}
                    disabled={disableNextWhenReadOnly && isReadOnly}
                >
                    Next
                </button>
            )}
        </div>
    );
}

export default function AdditionalInfoPage() {
    const [token, setToken] = useState(null);
    const [fieldValues, setFieldValues] = useState({});
    const [loading, setLoading] = useState(true);
    const [loadingText, setLoadingText] = useState('Loading...');
    const [loanid, setLoanid] = useState('');
    const [showToast, setShowToast] = useState(false);
    const [showToastText, setShowToastText] = useState('Loan saved successfully...');
    const [showErrorToast, setShowErrorToast] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [skipFetch, setSkipFetch] = useState(false);
    const [errorTitle, setErrorTitle] = useState("Submission Error");
    const [toastVisible, setToastVisible] = useState(false);
    const [toastAnimation, setToastAnimation] = useState("animate-fade-in-right");
    const [warningToastVisible, setWarningToastVisible] = useState(false);
    const [warningToastMessage, setWarningToastMessage] = useState("");
    const [initialSubmitDate, setInitialSubmitDate] = useState('');
    // Read-only before submittal too, once disclosures are out (see utils/loanEditLock).
    const [editLocked, setEditLocked] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [selectedBorrowerLegacyId, setSelectedBorrowerLegacyId] = useState('');
    const borrowerPairRef = useRef(null);
    const [fieldsJson, setFieldsJson] = useState([]);
    const [dirtyPromptSource, setDirtyPromptSource] = useState(null); // "sidebar" | "next"
    const [showDirtyPrompt, setShowDirtyPrompt] = useState(false);
    const [bpcPrograms, setBpcPrograms] = useState([]);
    const [hasLoCompHistory, setHasLoCompHistory] = useState(false);
    const [lockOccupancy, setLockOccupancy] = useState(false);
    const [correspondentChannel, setCorrespondentChannel] = useState(false);

    useEffect(() => {
        if (window.elli && window.elli.script && window.elli.script.guest) {
            window.elli.script.guest.create();
            try {
                window.elli.script.getObject("loan").then(async function (loanObject) {
                    loanObject.all().then(async function (loan) {
                        setLoanid(loan.id);
                        const tpo = loan?.tpo || {};
                        const submittedDate = tpo.initialSubmitDate || '';
                        setInitialSubmitDate(submittedDate);

                        // Fetch 2626 field to determine which JSON to load
                        try {
                            const tpoAuthObject = await window.elli.script.getObject("auth");
                            const authCode = await tpoAuthObject.createAuthCode();
                            const token = await getAccessToken(authCode);
                            // Use fieldReader to get 2626 value
                            const data = await fieldReader(token, ["2626"], loan.id);
                            const val2626 = (data["2626"] || "").toString().trim();
                            let json;
                            if (val2626?.toLowerCase() === "correspondent") {
                                console.log("Loading correspondent additional fields JSON");
                                json = await import('../../data/correspondent/additionalFields.json');
                                setCorrespondentChannel(true);
                            }
                            else {
                                console.log("Loading consumer additional fields JSON");
                                json = await import('../../data/consumer/additionalFields.json');
                            }
                            setFieldsJson(getAdditionalInfoFields(json.default || json));
                        } catch (e) {
                            // fallback to consumer if error
                            const json = await import('../../data/consumer/additionalFields.json');
                            setFieldsJson(getAdditionalInfoFields(json.default || json));
                        }

                        await getToken(false);
                    });
                });
            } catch (error) {
                const errorCode = logError("COMMERCIAL_APP_LOAN_FETCH_ERROR");
                logAppInsightError("Loan Fetch Error", {
                    component: "Index",
                    problemId: errorCode,
                    errorCode,
                    rawError: error,
                    errorMessage: `${errorCode} - Failed to fetch loan data from guest script.`
                });
                console.error("Auth block Error:", error);
            }
        } else {
            console.warn("elli.script.guest is not available.");
        }
    }, []);

    const getToken = async (skipFieldLoad = false) => {
        setLoading(true);
        try {
            const tpoAuthObject = await window.elli.script.getObject("auth");
            const authCode = await tpoAuthObject.createAuthCode();
            const token = await getAccessToken(authCode);
            setToken(token);
            setSkipFetch(skipFieldLoad);
        } catch (err) {
            const errorCode = logError("COMMERCIAL_APP_TOKEN_FETCH_ERROR");
            logAppInsightError("Token Fetch Error", {
                component: "Index",
                problemId: errorCode,
                errorCode,
                rawError: err,
                errorMessage: `${errorCode} - Failed to fetch access token for loan.`
            });
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!token || !loanid) return;
        setLoading(true);
        fetchFields();
    }, [loanid]);

    const fetchFields = async () => {
        const allFieldIds = [...new Set([
            "14",
            "19",
            "TPO.X16", // Org ID
            "TPO.X15",
            "TPO.X39",
            "1811",
            ...LOAN_EDIT_LOCK_FIELD_IDS,
            ...fieldsJson.map(field => field.id)
        ])];

        setLoading(true);
        let locked = false;
        try {
            const data = await fieldReader(token, allFieldIds, loanid);
            const merged = { ...data };
            locked = isLoanEditLocked(merged);
            setEditLocked(locked);

            setFieldValues(merged);

            // Use ONLY OrgId from TPO.X16
            try {
                const orgId = (merged["TPO.X16"] || "").trim();

                if (orgId) {
                    const programs = await getBPCPrograms(orgId); // pass OrgId
                    setBpcPrograms(programs);
                } else {
                    setBpcPrograms([]);
                }
            } catch (err) {
                console.error("Failed to fetch BPC Programs using OrgId (TPO.X16)", err);
            }

            setFieldValues(merged);

            // Lock Occupancy if already saved with value
            if (!correspondentChannel) {
                if (merged["1811"] && merged["1811"].trim() !== "") {
                    setLockOccupancy(true);
                } else {
                    setLockOccupancy(false);
                }
            }

            try {
                const tpoIdRaw = merged["TPO.X39"]?.trim()
                    || merged["TPO.X15"]?.trim()
                    || "";

                if (tpoIdRaw) {
                    const flag = await fetchLoCompHistoryFlag({ accessToken: token, tpoId: tpoIdRaw, isRecursive: true });

                    if (flag?.error) {
                        // safest default: disable if we cannot verify
                        setHasLoCompHistory(true);
                    } else {
                        setHasLoCompHistory(!!flag.hasHistory);
                    }
                } else {
                    setHasLoCompHistory(true);
                }
            } catch (e) {
                console.error("Failed to check loCompHistory", e);
                setHasLoCompHistory(true);
            }


        } catch (err) {
            const errorCode = logError("COMMERCIAL_APP_FIELD_FETCH_ERROR");
            logAppInsightError("Field Fetch Error", {
                component: "Index",
                problemId: errorCode,
                errorCode,
                rawError: err,
                errorMessage: `${errorCode} - Failed to fetch field values from Encompass.`
            });
            console.error("Field fetch failed", err);
        } finally {
            if ((initialSubmitDate && initialSubmitDate !== "//") || locked) {
                setTimeout(() => makeFormReadOnly(), 100);
            }
            setLoading(false);
        }
    };

    const makeFormReadOnly = () => {
        const selectors = [
            'input:not([type=hidden]):not([readonly]):not([data-force-edit])',
            'textarea:not([readonly]):not([data-force-edit])',
            'select:not([disabled]):not([data-force-edit])',
            'input[type=checkbox]:not([disabled]):not([data-force-edit])',
            'button:not([data-allow])'
        ];

        const elements = document.querySelectorAll(selectors.join(', '));

        elements.forEach(el => {
            // Skip borrower pair select and its icons
            if (
                el.id === 'borrower-pair' ||
                el.closest('#borrower-pair-wrapper') // wrap icons with this ID
            ) {
                return;
            }

            if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA') {
                el.setAttribute('readOnly', true);
            } else {
                el.setAttribute('disabled', true);
            }

            el.classList.add('opacity-60', 'cursor-not-allowed');
        });
    };

    useEffect(() => {
        if (!token) return;

        if (skipFetch) {
            setSkipFetch(false);
            return;
        }

        fetchFields();

    }, [token]);

    // reads the current UI value for a given field id using your existing conventions
    const getUiFieldValue = (rawId, fieldMeta) => {
        const escapedId = rawId.replace(/\./g, '\\.');
        const yesCheckbox = document.getElementById(`field-${rawId}-yes`);
        const noCheckbox = document.getElementById(`field-${rawId}-no`);

        // Pair of Yes/No checkboxes
        if (yesCheckbox && noCheckbox) {
            if (fieldMeta?.datatype === "YN") {
                return yesCheckbox.checked ? "true" : noCheckbox.checked ? "false" : "";
            }
            return yesCheckbox.checked ? "Y" : noCheckbox.checked ? "N" : "";
        }

        // Multiple checkboxes using same id (checkbox group)
        const groupChecks = document.querySelectorAll(`input[type="checkbox"]#field-${escapedId}`);
        if (groupChecks && groupChecks.length > 1) {
            const checked = [...groupChecks].find(cb => cb.checked);
            return checked?.value || "";
        }

        // Single input (text/select/single checkbox)
        const input = document.getElementById(`field-${rawId}`);
        if (!input) return "";

        if (input.type === 'checkbox') {
            // Keep compatibility with your writer logic
            if (fieldMeta?.datatype === "YN") return input.checked ? "true" : "false";
            if (fieldMeta?.datatype === "Y") return input.checked ? "true" : "";
            return input.checked ? (input.value || "true") : "";
        }

        if (fieldMeta?.datatype === "string" && input.tagName === "SELECT") {
            // For Save, use the selected OPTION's value (you already do this in Save path)
            return input.options[input.selectedIndex]?.value || "";
        }

        let val = input.value || "";
        if (fieldMeta?.datatype === "money" && typeof val === "string") {
            val = val.replace(/,/g, "");
        }
        return val;
    };

    const getFieldDef = (id) => {
        return fieldsJson.find(f => f.id === id);
    };

    // Normalize value for comparisons (unchanged)
    const normalizeValue = (fieldDef, raw) => {
        const v = (raw ?? "").toString().trim();

        if (fieldDef?.format === "YN" || fieldDef?.format === "YN_BOOL") {
            const up = v.toUpperCase();
            if (up === "Y" || up === "YES" || up === "TRUE") return "Yes";
            if (up === "N" || up === "NO" || up === "FALSE") return "No";
        }
        return v.toUpperCase();
    };

    // Helper: check emptiness with allowZero support
    const isEmptyForRule = (raw, { allowZero } = {}) => {
        if (raw === null || raw === undefined) return true;
        const v = raw.toString().trim();
        if (v === "") return true;
        if (allowZero && (v === "0" || v === "0.0" || v === "0.00")) return false;
        return false; // non-empty otherwise
    };

    const validateAdditionalJsonRules = () => {
        const errors = [];

        (fieldsJson || []).forEach(field => {
            if (!Array.isArray(field.rules) || field.rules.length === 0) return;

            const thisFieldValRaw = getUiFieldValue(field.id, field);
            const thisFieldDef = getFieldDef(field.id);
            const thisFieldVal = normalizeValue(thisFieldDef, thisFieldValRaw);

            field.rules.forEach(rule => {
                const trigger = rule?.if?.equals;
                if (trigger == null) return;

                const triggerNorm = normalizeValue(thisFieldDef, trigger);

                if (thisFieldVal === triggerNorm) {
                    const targetId = rule?.then?.field;
                    if (!targetId) return;

                    const targetDef = getFieldDef(targetId) || {};
                    const targetValRaw = getUiFieldValue(targetId, targetDef);

                    // 2a) Conditional "required" rule (with optional allowZero)
                    if (rule?.then?.required === true) {
                        const allowZero = !!rule?.then?.allowZero;
                        if (isEmptyForRule(targetValRaw, { allowZero })) {
                            errors.push(rule.message || `Field ${targetId} is required.`);
                            return; // skip further checks for this rule after required fail
                        }
                    }

                    // 2b) Conditional equality rule (already supported earlier)
                    if (rule?.then?.equals != null) {
                        const targetVal = normalizeValue(targetDef, targetValRaw);
                        const mustEqualNorm = normalizeValue(targetDef, rule.then.equals);
                        if (targetVal !== mustEqualNorm) {
                            errors.push(rule.message || `Field ${targetId} must equal ${rule.then.equals}.`);
                        }
                    }
                }
            });
        });

        return errors;
    };

    const runJsonDateValidation = () => {
        const errors = [];

        fieldsJson.forEach(f => {
            const v = getUiFieldValue(f.id, f);
            const valRules = f.validation || {};

            if (f.datatype === 'date' && v) {
                // normalize both to yyyy-mm-dd strings
                const parsedStr = new Date(v).toISOString().slice(0, 10);
                const todayStr = new Date().toISOString().slice(0, 10);

                if (valRules.noFuture && parsedStr > todayStr) {
                    errors.push(`${f.name} cannot be a future date.`);
                }
                if (valRules.noPast && parsedStr < todayStr) {
                    errors.push(`${f.name} cannot be a past date.`);
                }
            }
        });

        return errors;
    };

    const isRequiredNumberEmpty = (value) => {
        if (value === null || value === undefined) return true;

        const str = String(value).trim();
        if (str === "") return true;

        const num = Number(str);

        return Number.isNaN(num) || num === 0;
    };

    const handleSubmit = async (isRetry = false) => {
        // Clear previous input-only highlights
        document.querySelectorAll('[data-required-missing-input]').forEach(el => {
            el.removeAttribute('data-required-missing-input');
            el.style.border = '';
        });

        const planRaw = fieldValues?.['LCP.X1'] ?? "";
        const isBorrowerPlan = typeof planRaw === 'string' && /borrower/i.test(planRaw);

        // build required fields but skip 389 when not borrower plan
        const requiredFields = fieldsJson.filter(f => {
            if (!f.required) return false;
            if (f.id === '389' && !isBorrowerPlan) return false; // 389 only required for borrower-paid plan
            return true;
        });

        const missingFields = requiredFields.filter(field => {
            const el = document.getElementById(`field-${field.id}`);
            const value = el?.value;

            if (String(field.id) === "389") {
                return isRequiredNumberEmpty(value);
            }

            return value === null || value === undefined || String(value).trim() === "";
        });

        if (missingFields.length > 0) {


            const messages = [...missingFields.map(f => `• ${f.name}`)];
            let firstToFocus = null;
            // Highlight only the input/select/textarea — NOT the label or wrapper
            missingFields.forEach(f => {
                const el = document.getElementById(`field-${f.id}`);
                if (!el) return;

                el.setAttribute('data-required-missing-input', 'true');
                el.style.setProperty('border', '1px solid red', 'important');

                if (!firstToFocus && (el.tagName === 'INPUT' || el.tagName === 'SELECT' || el.tagName === 'TEXTAREA')) {
                    firstToFocus = el;
                }
            });

            if (firstToFocus) {
                try { firstToFocus.focus(); } catch (e) { /* ignore */ }
            }


            setWarningToastMessage(`Please fill all required fields:<br/> ${messages.join('<br/> ')}`);
            setWarningToastVisible(true);
            setTimeout(() => setWarningToastVisible(false), 5000);
            setLoading(false);
            return;
        }

        const crossErrors = validateAdditionalJsonRules();
        if (crossErrors.length > 0) {
            setWarningToastMessage(`Please fix the following before saving:<br/> ${crossErrors.map(e => `• ${e}`).join('<br/> ')}`);
            setWarningToastVisible(true);
            setTimeout(() => setWarningToastVisible(false), 5000);
            setLoading(false);
            return;
        }

        const dateErrors = runJsonDateValidation();
        if (dateErrors.length > 0) {
            setWarningToastMessage(
                `Please fix the following before saving:<br/> ${dateErrors.map(e => `• ${e}`).join('<br/> ')}`
            );
            setWarningToastVisible(true);
            setTimeout(() => setWarningToastVisible(false), 5000);
            setLoading(false);
            return;
        }

        setLoadingText('Saving...');
        setLoading(true);
        await Promise.resolve();

        const allowedFieldIds = new Set();

        const allFieldsForThisPage = [
            ...(fieldsJson.filter(f => f.isUpdate !== false).map(f => ({ ...f, IsUpdate: true })))
        ];

        const updatedFields = allFieldsForThisPage
            .map(f => {
                const rawId = f.id;
                const escapedId = rawId.replace(/\./g, '\\.');
                const fieldId = `field-${rawId}`;
                const fieldMeta = fieldsJson.find(field => field.id === rawId);

                const yesCheckbox = document.getElementById(`field-${rawId}-yes`);
                const noCheckbox = document.getElementById(`field-${rawId}-no`);

                let value;
                if (yesCheckbox && noCheckbox) {
                    // Yes/No checkbox pair logic
                    if (fieldMeta?.datatype === "YN") {
                        value = yesCheckbox.checked ? "true" : noCheckbox.checked ? "false" : "";
                    } else {
                        value = yesCheckbox.checked ? "Y" : noCheckbox.checked ? "N" : "";
                    }
                } else {
                    // Single checkbox or other inputs
                    const input = document.getElementById(fieldId);
                    if (input?.type === 'checkbox') {
                        if (fieldMeta?.datatype === "YN") {
                            value = input.checked ? "true" : "false";
                        }
                        else if (fieldMeta?.datatype === "Y") {
                            value = input.checked ? "true" : "";
                        }
                        else {
                            // Check if it's a checkbox group (multiple with same ID)
                            const checkboxes = document.querySelectorAll(`input[type="checkbox"]#field-${escapedId}`);
                            if (checkboxes.length > 1) {
                                const checked = [...checkboxes].find(cb => cb.checked);


                                return {
                                    id: f.id,
                                    value: checked?.value || fieldValues[rawId] || ""
                                };
                            }

                            // Handle regular input (text, single checkbox, etc.)
                            const input = document.getElementById(`field-${rawId}`);
                            if (input) {
                                if (input.type === 'checkbox') {
                                    if (!input.checked && fieldValues[rawId]) {
                                        value = fieldValues[rawId];
                                    } else {
                                        value = input.checked ? input.value || "true" : "";
                                    }
                                } else {
                                    value = input.value;
                                }
                            } else if (fieldValues[rawId]) {
                                value = fieldValues[rawId];
                            } else {
                                value = "";
                            }
                        }
                    } else {
                        if (fieldMeta?.datatype === "string" && input?.tagName === "SELECT") {
                            value = input.options[input.selectedIndex]?.value || "";
                        } else {
                            value = input?.value || "";
                            if (fieldMeta?.datatype === "money" && typeof value === "string") {
                                value = value.replace(/,/g, "");
                            }
                        }
                    }
                }

                return {
                    id: f.id,
                    value
                };
            })
            .filter(f => {
                // Skip asset fields that are not in the allowedFieldIds set
                const baseId = f.id.split("#")[0]; // for DD0102#1
                if (baseId.startsWith("DD") && !allowedFieldIds.has(baseId)) {
                    return false;
                }
                return true;
            });

        try {

            const externalOnly = ["LCP.X1", "389"];
            const encompassFields = updatedFields.filter(f => !externalOnly.includes(f.id));
            const externalFields = updatedFields.filter(f => externalOnly.includes(f.id));

            if (externalFields.length) {
                const tpoAuthObject = await window.elli.script.getObject("auth");
                const authCode = await tpoAuthObject.createAuthCode();
                await fieldWriterExternal(authCode, loanid, externalFields);
            }

            await fieldWriter(token, loanid, encompassFields);

            //Clear dirty flag after successful save
            localStorage.removeItem(DIRTY_FLAG_KEY);
            window.dispatchEvent(new StorageEvent("storage", { key: DIRTY_FLAG_KEY, newValue: "false" }));


            if (!isRetry) {
                try {
                    const evalResult = await evaluateAndRemoveIncomes(
                        token,
                        loanid,
                        updatedFields,
                        fieldValues
                    );

                    if (evalResult.triggered && evalResult.matched) {
                        setLoading(true);
                        fetchFields();
                    }

                    if (borrowerPairRef.current?.reload) {
                        borrowerPairRef.current.reload(selectedBorrowerLegacyId);
                    }
                } catch (submissionError) {
                    const errorCode = logError("COMMERCIAL_APP_REO_VOD_SUBMIT_ERROR");
                    logAppInsightError("REO/VOD Submission Error", {
                        component: "Index",
                        problemId: errorCode,
                        errorCode,
                        rawError: submissionError,
                        errorMessage: `${errorCode} - Failed to submit REO or VOD data to Encompass.`
                    });
                    throw submissionError; // Re-throw so outer catch handles retry or toast
                }

                setShowToastText('Loan saved successfully...');
                setShowToast(true);

                // If 1811 now has value after save → lock it
                if (!correspondentChannel) {
                    const occValue = updatedFields.find(f => f.id === "1811")?.value;
                    if (occValue && occValue.trim() !== "") {
                        setLockOccupancy(true);
                    }
                }
                setTimeout(() => setShowToast(false), 10000);
            }
        } catch (error) {
            const errorCode = logError("COMMERCIAL_APP_FIELD_WRITE_ERROR");
            logAppInsightError("Field Write Error", {
                component: "Index",
                problemId: errorCode,
                errorCode,
                rawError: error,
                errorMessage: `${errorCode} - Failed to write field data to Encompass.`
            });

            if (error.response?.status === 401 && !isRetry) {
                console.warn('401 detected, regenerating token...');
                await getToken(true);
                return handleSubmit(true);
            }

            const json = error.response?.data || {};
            const title = determineErrorTitle(json);
            setErrorTitle(title);

            const mainDetail = json.details || "Submission failed.";
            const errorMessages = generateFieldErrorMessages(json.errors || [], updatedFields, fieldsJson);
            const fullMessage = `${mainDetail}${errorMessages ? "\n" + errorMessages : ""}`;

            setErrorMessage(fullMessage);
            setShowErrorToast(true);
        } finally {
            setLoading(false);
        }

    };

    const generateFieldErrorMessages = (errors, payload, fieldsJson) => {
        return errors.map((error) => {
            const match = error.summary.match(/changes\[(\d+)\]\.value/);
            if (match) {
                const index = parseInt(match[1], 10);
                const payloadField = payload[index];
                const fieldId = payloadField?.id;
                const fieldMeta = fieldsJson.find((field) => field.id === fieldId);
                const fieldName = fieldMeta?.name || `Field ID ${fieldId}`;
                return `${fieldName} (${fieldId}): ${error.details}`;
            }
            return `${error.summary}\n${error.details}`;
        }).join('\n');
    };

    const determineErrorTitle = (json) => {
        if (json.summary === "Conflict" && json.details?.includes("locked by another user")) {
            return "Loan Locked by Another User";
        }

        if (json.summary === "Bad Request" && Array.isArray(json.errors) && json.errors.length > 0) {
            return "Invalid Fields Detected";
        }

        return "Submission Error";
    };

    useEffect(() => {
        if (showToast) {
            setToastVisible(true);
            setToastAnimation("animate-fade-in-right");

            const hideTimer = setTimeout(() => {
                setToastAnimation("animate-fade-out-right");

                const vanishTimer = setTimeout(() => {
                    hideToast();
                }, 500);

                return () => clearTimeout(vanishTimer);
            }, 4000);

            return () => clearTimeout(hideTimer);
        }
    }, [showToast]);

    const handleFieldChange = (fieldId, value) => {
        setFieldValues(prev => {
            const prevVal = prev?.[fieldId] == null ? "" : String(prev[fieldId]);
            const newVal = value == null ? "" : String(value);

            if (prevVal === newVal) {
                return prev;
            }

            return {
                ...prev,
                [fieldId]: value
            };
        });
    };

    const hideToast = () => {
        setShowToast(false);
        setToastVisible(false);
    };

    const isReadOnly = (initialSubmitDate && initialSubmitDate !== "//") || editLocked;

    const handleNext = async () => {
        const isDirty = localStorage.getItem(DIRTY_FLAG_KEY) === "true";

        if (isDirty) {
            setDirtyPromptSource("next");
            setShowDirtyPrompt(true);
            return;
        }

        if (loanid) {
            await redirectToPage("URLA:Lender Loan Information", loanid);
        }
    };

    useEffect(() => {
        const markDirty = () => {
            localStorage.setItem(DIRTY_FLAG_KEY, "true");
        };

        const handleFieldChange = (e) => {
            // Only trigger if inside AdditionalInfoControl
            const container = e.target.closest('.additional-info-control-wrapper');
            if (!container) return; // ignore if not inside AdditionalInfoControl

            const tag = e.target.tagName;
            const type = e.target.type;

            if (
                tag === "INPUT" ||
                tag === "SELECT" ||
                tag === "TEXTAREA" ||
                (tag === "BUTTON" && type === "checkbox")
            ) {
                markDirty();
            }
        };

        document.addEventListener("input", handleFieldChange);
        document.addEventListener("change", handleFieldChange);

        return () => {
            document.removeEventListener("input", handleFieldChange);
            document.removeEventListener("change", handleFieldChange);
        };
    }, []);


    const channel = new BroadcastChannel('tpo_dirty_check_channel');

    useEffect(() => {
        const handleMessage = (event) => {
            if (event.data?.type === "TRIGGER_DIRTY_CHECK") {
                const isDirty = localStorage.getItem(DIRTY_FLAG_KEY) === "true";

                if (isDirty) {
                    setDirtyPromptSource("sidebar");
                    setShowDirtyPrompt(true);
                } else {
                    channel.postMessage({ type: "DIRTY_CHECK_RESULT", approved: true });
                }
            }
        };

        channel.addEventListener("message", handleMessage);
        return () => channel.removeEventListener("message", handleMessage);
    }, []);

    //Confirm / Cancel for Sidebar-triggered dirty prompt
    const handleSidebarDirtyConfirm = () => {
        localStorage.removeItem(DIRTY_FLAG_KEY);
        channel.postMessage({ type: "DIRTY_CHECK_RESULT", approved: true });
        setShowDirtyPrompt(false);
        setDirtyPromptSource(null);
    };

    const handleSidebarDirtyCancel = () => {
        channel.postMessage({ type: "DIRTY_CHECK_RESULT", approved: false });
        setShowDirtyPrompt(false);
        setDirtyPromptSource(null);
    };

    //Confirm / Cancel for Next-button-triggered dirty prompt
    const handleNextDirtyConfirm = async () => {
        localStorage.removeItem(DIRTY_FLAG_KEY);
        setShowDirtyPrompt(false);
        setDirtyPromptSource(null);

        if (loanid) {
            await redirectToPage("URLA:Lender Loan Information", loanid);
        }
    };

    const handleNextDirtyCancel = () => {
        setShowDirtyPrompt(false);
        setDirtyPromptSource(null);
    };

    useEffect(() => {
        localStorage.removeItem(DIRTY_FLAG_KEY);
    }, []);

    return (
        <>
            <LoadingOverlay loading={loading || isSaving} text={loadingText || 'Saving...'} />

            <Toast
                visible={toastVisible}
                type="success"
                message={showToastText}
                onClose={hideToast}
                animationClass={toastAnimation}
            />

            <Toast
                visible={warningToastVisible}
                type="warning"
                message={warningToastMessage}
                onClose={() => setWarningToastVisible(false)}
                animationClass="animate-fade-in-right"
            />

            <DirtyCheckPrompt
                visible={showDirtyPrompt}
                onConfirm={async () => {
                    setShowDirtyPrompt(false);

                    try {
                        await handleSubmit(false); // run save logic first
                        //Only continue if save succeeded and dirty flag cleared
                        const stillDirty = localStorage.getItem(DIRTY_FLAG_KEY) === "true";

                        if (!stillDirty) {
                            // After successful save → use same confirm handler logic
                            setTimeout(async () => {
                                if (dirtyPromptSource === "next") {
                                    await handleNextDirtyConfirm();
                                } else {
                                    handleSidebarDirtyConfirm();
                                }
                            }, 2000);
                        } else {
                            console.warn("Save failed — dirty flag still set. Navigation stopped.");
                        }

                    } catch (err) {
                        console.error("Error during Save in DirtyCheckPrompt:", err);
                    } finally {
                        setLoading(false);
                    }
                }}
                onDisregard={() => {
                    // skip saving — just reuse confirm flow directly
                    if (dirtyPromptSource === "next") {
                        handleNextDirtyConfirm();
                    } else {
                        handleSidebarDirtyConfirm();
                    }
                }}
                onCancel={() => {
                    // stay on the same page
                    if (dirtyPromptSource === "next") {
                        handleNextDirtyCancel();
                    } else {
                        handleSidebarDirtyCancel();
                    }
                }}
            />

            <Modal
                title={errorTitle}
                message={errorMessage}
                isOpen={showErrorToast}
                onClose={() => setShowErrorToast(false)}
                type="error"
            />

            <div className="space-y-10 p-25px">
                <div className="rounded-lg p-3 space-y-6 max-w-8xl mx-auto">
                    <h2 className="page-title">URLA 2020 / Additional Information</h2>
                    <div className="flex flex-wrap justify-between items-start gap-4">
                        <div className="flex-grow">
                            <BorrowerPairSection
                                ref={borrowerPairRef}
                                token={token}
                                loanid={loanid}
                                onSelect={(applicationId, index, legacyId) => {
                                    setSelectedBorrowerLegacyId(legacyId);
                                }}
                                showToast={setShowToast}
                                setToastText={setShowToastText}
                                showWarningToast={setWarningToastVisible}
                                setWarningMessage={setWarningToastMessage}
                                isReadOnly={isReadOnly}
                                setLoading={setIsSaving}
                            />
                        </div>

                        <ActionButtons
                            isReadOnly={isReadOnly}
                            showNext={!correspondentChannel}
                            onSave={handleSubmit}
                            onNext={handleNext}
                            containerClass="pt-0 pr-4 pb-4 pl-4"
                        />
                    </div>

                    <div className='additional-info-control-wrapper'>
                        <AdditionalInfoControl
                            additionalFields={fieldsJson}
                            fieldValues={fieldValues}
                            bpcPrograms={bpcPrograms}
                            hasLoCompHistory={hasLoCompHistory}
                            onFieldChange={handleFieldChange}
                            lockOccupancy={lockOccupancy}
                            isCorrespondent={correspondentChannel}
                            isReadOnly={isReadOnly}
                        />
                    </div>
                    <ActionButtons
                        isReadOnly={isReadOnly}
                        showNext={!correspondentChannel}
                        onSave={handleSubmit}
                        onNext={handleNext}
                        containerClass="justify-end mt-4"
                    />
                </div>
            </div>
        </>
    );
}
